// useDriveSession.js              me/logout + authed state

import { useEffect, useCallback, useRef } from 'react';
import env from '../constants/env';
import google from '../constants/google';

export default function useDriveSession({ setAuthed, setFiles, setError, setPath, setTree, setRoots,
    serv, setShowTree,
    setExpanded, filesCacheRef, inflightRef, folderKidsCacheRef, kidsInflightRef, featuresManager,
}) {
    const featuresRef = useRef(featuresManager.features);

    useEffect(() => {
        featuresRef.current = featuresManager.features;
    }, [featuresManager]);

    const initOnceRef = useRef(false);

    function asArrayOrNull(v) {
        if (Array.isArray(v)) return v;
        if (v === null) return null;
        return [];
    }

    const resetSession = useCallback(() => {
        setAuthed(false);
        setFiles([]);
        setError('');
        setPath([]);
        setTree({});
        setRoots([]);
        setExpanded({});
        // очистимо кеші
        filesCacheRef.current.clear();
        inflightRef.current.clear();
        folderKidsCacheRef.current.clear();
        kidsInflightRef.current.clear();
        // прибрати #auth=ok
        if (window.location.hash.includes('auth=')) {
            const url = new URL(window.location.href);
            url.hash = '';
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    function createUrl(key, checked) {
        let url = `/drive.php?action=home&includeDrives=`;
        if (key === 'sharedDrives')
            url += (checked ? `1` : `0`);
        else
            url += (featuresRef.current.sharedDrives.requested ? `1` : `0`);
        url += `&includeSharedWithMe=`;
        if (key === 'sharedWithMe')
            url += (checked ? `1` : `0`);
        else
            url += (featuresRef.current.sharedWithMe.requested ? `1` : `0`);
        return url;
    }

    const loadForest = useCallback(async (key = '', checked = false) => {
        setError('');
        try {
            const url = createUrl(key, checked);
            const r = await fetch(url, { credentials: 'include' });

            const j = await serv.safeJson(r);
            if (!r.ok) throw new Error(j?.error || 'Forest load failed');

            const forest = j.files || [];

            // console.log('forest');
            // console.log(forest);

            // Хелпер: підхопити всі корисні поля, включно з shortcut
            const pickNode = (n) => ({
                id: n.id,
                name: n.name,
                icon: n.icon,
                type: n.type,
                mtime: n.mtime,
                url: n.url,
                thumb: n.thumb,
                shortcut: n.shortcut
                    ? {
                        targetId: n.shortcut.targetId ?? null,
                        targetMimeType: n.shortcut.targetMimeType ?? null,
                    }
                    : null,
                // Похідні прапорці
                isShortcut: (n.type ?? n.mimeType) === 'application/vnd.google-apps.shortcut',
                isShortcutFolder: ((n.shortcut?.targetMimeType ?? n.shortcutDetails?.targetMimeType) === 'application/vnd.google-apps.folder'),
            });

            // список для правої панелі — не губимо shortcut
            setFiles(serv.sortAscByMtime(forest.map(pickNode)));

            // «корені» (якщо потрібно лише id+name — залиши як було;
            setRoots(forest.map((r) => ({ id: r.id, name: r.name })));

            // дерево: і в коренях, і в дітях зберігаємо shortcut
            setTree(() => {
                const t = {};
                for (const r of forest) {
                    const rootNode = pickNode(r);
                    t[r.id] = {
                        ...rootNode,
                        children: asArrayOrNull(r.children)?.map((c) => pickNode(c)) ?? null,
                    };
                    // плейсхолдери для дітей (children=null), але збережемо метадані + shortcut
                    if (Array.isArray(r.children))
                        for (const c of r.children ?? []) {
                            t[c.id] = { ...pickNode(c), children: null };
                        }
                }
                return t;
            });

            setShowTree(true);
        } catch (e) {
            setError(e.message);
        }
    }, [featuresManager]);

    // ==== GIS init
    useEffect(() => {
        const init = () => {
            window.__codeClient = window.google.accounts.oauth2.initCodeClient({
                client_id: env.CLIENT_ID,
                scope: google.SCOPE,
                ux_mode: google.UX_MODE,
                redirect_uri: env.REDIRECT_URI,
            });
        };
        if (window.google?.accounts?.oauth2) { init(); return; }
        const s = document.createElement('script');
        s.src = google.S_SRC;
        s.async = true; s.defer = true; s.onload = init;
        document.body.appendChild(s);
        return () => { try { document.body.removeChild(s); } catch { /*   */ } };
    }, []);

    // ==== session / auto
    useEffect(() => {
        if (initOnceRef.current) return;
        initOnceRef.current = true;

        (async () => {
            try {
                const r = await fetch('/drive.php?action=me', { credentials: 'include' });
                const j = r.ok ? await serv.safeJson(r) : null;
                const hasSession = !!j?.sub || window.location.hash.includes('auth=ok');
                if (hasSession) {
                    setAuthed(true);
                    loadForest();
                }
            } catch {/* noop */ }
        })();
    }, []);

    useEffect(() => {
        if (window.location.hash.includes('auth=ok')) setAuthed(true);
    }, []);

    const handleLogout = useCallback(async () => {
        try {
            const r = await fetch('/drive.php?action=logout', { credentials: 'include' });
            await serv.safeJson(r);
        } catch {/* ігноруємо мережеві глітчі */ }
        resetSession();
    }, [resetSession]);


    return {
        handleLogout, resetSession, loadForest,
    };

}